// retirementCompareCalculator.js

function totalMonths(years, months = 0) {
  return Number(years) * 12 + Number(months || 0);
}

function pctOfFRA(monthsFromFRA) {
  if (monthsFromFRA === 0) return 1;

  if (monthsFromFRA < 0) {
    // early reduction: first 36 months use 5/9%/month; after that 5/12%/month
    const monthsEarly = -monthsFromFRA;
    let reduction = 0;
    if (monthsEarly <= 36) {
      reduction = (monthsEarly * (5 / 9)) / 100;
    } else {
      reduction = (36 * (5 / 9) + (monthsEarly - 36) * (5 / 12)) / 100;
    }
    return 1 - reduction;
  } else {
    // delayed credits: 2/3 of 1% per month after FRA
    return 1 + monthsFromFRA * (2 / 3 / 100);
  }
}

export function ageAtBreakeven(
  claimAgeYears,
  claimAgeMonths,
  monthsToBreakEven
) {
  const totalMonths = claimAgeYears * 12 + claimAgeMonths + monthsToBreakEven;
  const years = Math.floor(totalMonths / 12);
  const months = Math.round(totalMonths % 12);
  const formatted = `${years} years and ${months} months`;
  return { years, months, decimal: totalMonths / 12, formatted };
}

/**
 * compareRetirementAges
 * - firstAge: earlier claiming age
 * - secondAge: later claiming age
 * - fraYears, fraMonths: full retirement age (e.g., 67,0)
 * - fraBenefit: monthly benefit at FRA (e.g., 2300)
 * - options:
 *    - cola (annual, default 0.02)
 *    - tvmRate (annual, default 0.03) -- used together with cola depending on fvMode
 *    - fvMode: "compound" | "colaOnly" | "net" | "customGrowth"
 *         "compound": growth factor per year = (1+cola)*(1+tvmRate) - 1  (default)
 *         "colaOnly": grow only by cola
 *         "net": grow by (1+cola)/(1+tvmRate) - 1
 *         "customGrowth": pass options.growth (annual number like 0.05)
 */

export function compareRetirementAges(
  firstAgeYears,
  firstAgeMonths,
  secondAgeYears,
  secondAgeMonths,
  fraYears,
  fraMonths,
  fraBenefit,
  options = {}
) {
  const {
    cola = 0.02,
    tvmRate = 0.03,
    fvMode = "compound", // "compound" | "colaOnly" | "net" | "customGrowth"
    growth = undefined, // used if fvMode === "customGrowth"
    roundMonths = true,
  } = options || {};

  // totals
  const fraTotal = totalMonths(fraYears, fraMonths);
  const firstTotal = totalMonths(firstAgeYears, firstAgeMonths);
  const secondTotal = totalMonths(secondAgeYears, secondAgeMonths);

  // months from FRA
  const firstFromFRA = firstTotal - fraTotal;
  const secondFromFRA = secondTotal - fraTotal;

  // monthly benefits at those ages
  const firstBenefit = Number(fraBenefit) * pctOfFRA(firstFromFRA);
  const secondBenefit = Number(fraBenefit) * pctOfFRA(secondFromFRA);

  // pre-FRA accumulation (total payments from earlier start until later start)
  const monthsBetween = Math.max(0, secondTotal - firstTotal);
  const preFRA = firstBenefit * monthsBetween;

  // ----------------- WITHOUT TVM (matches your Excel) -----------------
  // months to break-even = preFRA / laterMonthlyBenefit
  // const monthsToBreakEven_noTVM =
  //   secondBenefit > 0 ? preFRA / secondBenefit : 0;
  const benefitDifference = secondBenefit - firstBenefit;
  const monthsToBreakEven_noTVM =
    benefitDifference > 0 ? preFRA / benefitDifference : 0;

  const monthsToBreakEven_noTVM_display = roundMonths
    ? Math.round(monthsToBreakEven_noTVM)
    : monthsToBreakEven_noTVM;

  // ✅ FIX: include both years and months of later start age
  const be_noTVM = ageAtBreakeven(
    secondAgeYears,
    secondAgeMonths,
    monthsToBreakEven_noTVM_display
  );
  // ----------------- WITH TVM & COLA -----------------
  // choose growth factor per year depending on fvMode
  const getGrowthFactor = (colaLocal, tvmLocal, mode, customGrowth) => {
    if (mode === "compound") return (1 + colaLocal) * (1 + tvmLocal) - 1;
    if (mode === "colaOnly") return colaLocal;
    if (mode === "net") return (1 + colaLocal) / (1 + tvmLocal) - 1;
    if (mode === "customGrowth") return customGrowth || 0;
    return (1 + colaLocal) * (1 + tvmLocal) - 1;
  };

  const annualGrowth = getGrowthFactor(cola, tvmRate, fvMode, growth);

  // future value at later age: grow each pre-early monthly payment forward to the later start using annualGrowth
  let fv = 0;
  for (let i = 0; i < monthsBetween; i++) {
    const monthsUntilLater = monthsBetween - i;
    const factor = Math.pow(1 + annualGrowth, monthsUntilLater / 12);
    fv += firstBenefit * factor;
  }

  const monthsToBreakEven_TVM =
    benefitDifference > 0 ? fv / benefitDifference : 0;
  const monthsToBreakEven_TVM_display = roundMonths
    ? Math.round(monthsToBreakEven_TVM)
    : monthsToBreakEven_TVM;

  // ✅ FIX: include both years and months here as well
  const be_TVM = ageAtBreakeven(
    secondAgeYears,
    secondAgeMonths,
    monthsToBreakEven_TVM_display
  );

  return {
    // inputs & intermediate
    firstAge: `${firstAgeYears} years ${firstAgeMonths} months`,
    secondAge: `${secondAgeYears} years ${secondAgeMonths} months`,
    monthsFromFRA_first: firstFromFRA,
    monthsFromFRA_second: secondFromFRA,
    "%FRA_first": (pctOfFRA(firstFromFRA) * 100).toFixed(2),
    "%FRA_second": (pctOfFRA(secondFromFRA) * 100).toFixed(2),

    // benefits
    firstBenefit: Number(firstBenefit.toFixed(2)),
    secondBenefit: Number(secondBenefit.toFixed(2)),

    // no TVM
    preFRA: Number(preFRA.toFixed(2)),
    monthsToBreakEven_noTVM: monthsToBreakEven_noTVM_display,
    ageAtBreakEven_noTVM_formated: be_noTVM.formatted,
    ageAtBreakEven_noTVM_years: be_noTVM.years,
    ageAtBreakEven_noTVM_months: be_noTVM.months,

    // TVM
    futureValueAtLaterStart: Number(fv.toFixed(2)),
    monthsToBreakEven_TVM: monthsToBreakEven_TVM_display,
    ageAtBreakEven_TVM_formated: be_TVM.formatted,
    ageAtBreakEven_TVM_years: be_TVM.years,
    ageAtBreakEven_TVM_months: be_TVM.months,

    // internal numeric values if needed
    __raw: {
      monthsToBreakEven_noTVM_exact: monthsToBreakEven_noTVM,
      monthsToBreakEven_TVM_exact: monthsToBreakEven_TVM,
      annualGrowthUsed: annualGrowth,
    },
  };
}

/**
 * calibrateTVMRateToMatchMonths
 * - binary-search tvmRate (used in 'compound' mode) which yields monthsToBreakEven_TVM close to targetMonths.
 * - returns the tvmRate (annual decimal) that matches (approx).
 */
export function calibrateTVMRateToMatchMonths(
  targetMonths,
  firstAgeYears,
  firstAgeMonths,
  secondAgeYears,
  secondAgeMonths,
  fraYears,
  fraMonths,
  fraBenefit,
  cola = 0.02,
  options = {}
) {
  // search range for tvmRate
  let lo = 0;
  let hi = 0.3; // 30% annual as a safe upper bound
  for (let iter = 0; iter < 60; iter++) {
    const mid = (lo + hi) / 2;
    const res = compareRetirementAges(
      firstAgeYears,
      firstAgeMonths,
      secondAgeYears,
      secondAgeMonths,
      fraYears,
      fraMonths,
      fraBenefit,
      { cola, tvmRate: mid, fvMode: "compound", roundMonths: false, ...options }
    );
    const months = res.__raw.monthsToBreakEven_TVM_exact;
    if (months < targetMonths) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}
